import { supabase } from '@/core/infrastructure/supabase/supabaseClient';
import { ChatLocalDB, ChatMessage, OutboxMessage } from '@/core/infrastructure/local/ChatLocalDB';
import { RealtimeChannel } from '@supabase/supabase-js';

// ─── Criptografía Simétrica (WebCrypto API) ────────────────────────────────

async function deriveKey(citaId: string, idA: string, idB: string): Promise<CryptoKey> {
  // Ordenar IDs para garantizar que ambos bandos generen la misma semilla
  const sortedIds = [idA, idB].sort((a, b) => a.localeCompare(b));
  const seed = `${citaId}:${sortedIds[0]}:${sortedIds[1]}`;
  
  const encoder = new TextEncoder();
  const data = encoder.encode(seed);
  
  // Derivamos un hash SHA-256 (32 bytes = 256 bits)
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  
  // Importamos el hash como llave AES-GCM
  return crypto.subtle.importKey(
    'raw',
    hashBuffer,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
}

function bufferToBase64(buffer: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCodePoint(bytes[i]);
  }
  return btoa(binary);
}

function base64ToBuffer(base64: string): ArrayBuffer {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.codePointAt(i) ?? 0;
  }
  return bytes.buffer;
}

async function encryptMessage(text: string, key: CryptoKey): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encodedText = new TextEncoder().encode(text);
  
  const cipherBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    key,
    encodedText
  );
  
  // Concatenar IV + CipherText para poder desencriptar luego
  const combined = new Uint8Array(12 + cipherBuffer.byteLength);
  combined.set(iv, 0);
  combined.set(new Uint8Array(cipherBuffer), 12);
  
  return bufferToBase64(combined.buffer);
}

async function decryptMessage(cipherBase64: string, key: CryptoKey): Promise<string> {
  try {
    const combinedBuffer = base64ToBuffer(cipherBase64);
    const combined = new Uint8Array(combinedBuffer);
    
    const iv = combined.slice(0, 12);
    const cipherText = combined.slice(12);
    
    const decryptedBuffer = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv },
      key,
      cipherText
    );
    
    return new TextDecoder().decode(decryptedBuffer);
  } catch (error) {
    console.error('Error al desencriptar mensaje:', error);
    return '🔒 [Mensaje corrupto o llave incorrecta]';
  }
}

// ─── Motor del Chat (Capa de Aplicación) ───────────────────────────────────

export class ChatEngine {
  private readonly citaId: string;
  private readonly myId: string;
  private readonly otherId: string;
  private channel: RealtimeChannel | null = null;
  private cryptoKey: CryptoKey | null = null;
  private onMessageReceived: ((msg: ChatMessage) => void) | null = null;

  constructor(citaId: string, myId: string, otherId: string) {
    this.citaId = citaId;
    this.myId = myId;
    this.otherId = otherId;
  }

  /**
   * Inicializa la llave criptográfica, carga el historial local
   * y se suscribe al túnel de Supabase.
   */
  async start(onMessage: (msg: ChatMessage) => void): Promise<ChatMessage[]> {
    this.onMessageReceived = onMessage;
    
    // 1. Derivar Llave
    this.cryptoKey = await deriveKey(this.citaId, this.myId, this.otherId);

    // 2. Conectar al Túnel Broadcast
    const channelName = `chat_transaccional:${this.citaId}`;
    this.channel = supabase.channel(channelName, {
      config: { broadcast: { ack: true } } // ack: true para confirmar entrega en red
    });

    this.channel.on('broadcast', { event: 'new_message' }, async (payload) => {
      const encryptedMsg = payload.payload as ChatMessage;
      // Validar si el mensaje ya está en DB local (ej. si fue enviado por uno mismo desde otra pestaña)
      
      if (!this.cryptoKey) {
        console.error('Error: Mensaje recibido pero la llave no está inicializada');
        return;
      }

      const decryptedText = await decryptMessage(encryptedMsg.texto, this.cryptoKey);
      
      const clearMsg: ChatMessage = {
        ...encryptedMsg,
        texto: decryptedText
      };
      
      // Guardar en Sovereign Storage (Local-First)
      // Como guardamos en local de forma encriptada, guardamos el original (encryptedMsg)
      // Pero para este MVP sin complicar, guardaremos en texto plano en la DB o podemos guardar encriptado.
      // Siguiendo la regla de seguridad estricta: guardamos ENCRIPTADO en IndexedDB.
      await ChatLocalDB.saveMessage(encryptedMsg);
      
      // Notificar a la UI con el texto plano
      if (this.onMessageReceived) {
        this.onMessageReceived(clearMsg);
      }
    }).subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        this.flushOutbox(); // Al conectarnos, intentamos enviar los pendientes
      }
    });

    // 3. Sincronizar Historial desde el Servidor
    try {
      const { data: serverMsgs, error } = await supabase
        .from('citas_chat_log')
        .select('*')
        .eq('cita_id', this.citaId);
      
      if (!error && serverMsgs) {
        await ChatLocalDB.saveMessagesBatch(serverMsgs as ChatMessage[]);
      } else if (error) {
        console.error('Error sincronizando chat desde el servidor:', error);
      }
    } catch (e) {
      console.error('Error de red al sincronizar chat:', e);
    }

    // 4. Cargar Historial Local (Desencriptando al vuelo)
    const localEncryptedMsgs = await ChatLocalDB.getMessagesByCita(this.citaId);
    
    const decryptedMsgs: ChatMessage[] = [];
    for (const msg of localEncryptedMsgs) {
      const plainText = await decryptMessage(msg.texto, this.cryptoKey);
      decryptedMsgs.push({ ...msg, texto: plainText });
    }

    return decryptedMsgs;
  }

  /**
   * Envía un nuevo mensaje.
   * Lo guarda encriptado en el Outbox y luego intenta enviarlo al túnel.
   */
  async sendMessage(textoPlano: string): Promise<ChatMessage> {
    if (!this.cryptoKey) throw new Error('ChatEngine no inicializado');

    const encryptedText = await encryptMessage(textoPlano, this.cryptoKey);
    
    const msg: ChatMessage = {
      id: crypto.randomUUID(),
      cita_id: this.citaId,
      emisor_id: this.myId,
      texto: encryptedText, // Solo texto cifrado a la BD Local y a la red
      timestamp: new Date().toISOString()
    };

    // 1. Guardar en historial local y outbox
    await ChatLocalDB.saveMessage(msg);
    
    const outboxMsg: OutboxMessage = { ...msg, estado: 'pendiente', intentos: 0 };
    await ChatLocalDB.addToOutbox(outboxMsg);

    // 2. Notificar a la UI (con texto plano para mostrarlo inmediatamente sin recargar)
    const uiMsg: ChatMessage = { ...msg, texto: textoPlano };
    if (this.onMessageReceived) {
      this.onMessageReceived(uiMsg);
    }

    // 3. Intentar envío
    this.flushOutbox();

    return uiMsg;
  }

  /**
   * Sincronizador de fondo: Lee el outbox e intenta enviar a Supabase.
   */
  private async flushOutbox() {
    if (!this.channel) return;
    
    const pending = await ChatLocalDB.getPendingOutbox();
    
    for (const msg of pending) {
      try {
        await ChatLocalDB.markOutboxAsSending(msg.id);
        
        // 1. Guardar en base de datos remota
        const { error } = await supabase
          .from('citas_chat_log')
          .insert({
            id: msg.id,
            cita_id: msg.cita_id,
            emisor_id: msg.emisor_id,
            texto: msg.texto,
            timestamp: msg.timestamp
          });

        if (error) {
          throw new Error(`Error de base de datos al guardar mensaje: ${error.message}`);
        }

        // 2. Transmitir por el canal Realtime (Broadcast)
        await this.channel.send({
          type: 'broadcast',
          event: 'new_message',
          payload: {
            id: msg.id,
            cita_id: msg.cita_id,
            texto: msg.texto,
            emisor_id: msg.emisor_id,
            timestamp: msg.timestamp
          }
        });

        // Si tuvo éxito, lo sacamos del outbox
        await ChatLocalDB.removeFromOutbox(msg.id);
      } catch (err) {
        console.error('Error enviando mensaje, quedará en Outbox:', err);
        await ChatLocalDB.markOutboxAsError(msg.id);
      }
    }
  }

  /**
   * Limpieza al desmontar el componente
   */
  stop() {
    this.onMessageReceived = null;
    if (this.channel) {
      supabase.removeChannel(this.channel);
      this.channel = null;
    }
  }
}
